/**
 * Suivi des lots de renouvellement forcé de mot de passe déclenchés depuis
 * l'action support `PasswordExpiration.vue` (`POST /api/sa/password/expire`,
 * traité en jobs asynchrones groupés en `Bus::batch()` côté backend — voir
 * `/tmp/password-policy.md` §11.5/§11.6).
 *
 * Objectif de ce module : survivre à un démontage du composant (navigation
 * ailleurs dans l'app puis retour sur l'écran). Le composant reprend le
 * polling à partir de l'état ici stocké au lieu de perdre le fil, et le
 * bouton de lancement reste désactivé pour un mode tant que son dernier état
 * connu n'est pas `finished` — y compris juste après un retour sur l'écran,
 * avant le premier re-poll (fail-safe : mieux vaut bloquer un instant de trop
 * qu'autoriser un doublon).
 *
 * Un mode peut avoir plusieurs lots en vol simultanément : chaque identifiant
 * saisi dans la combobox (`user_id`/`vendor_id`/`accounting_firm_id`) déclenche
 * son propre appel `POST`, donc son propre `batch_id` (le mode `all` n'en a
 * jamais qu'un seul). `isModeRunning`/`aggregateForMode` agrègent l'ensemble
 * des lots d'un mode : on considère l'action de ce type "en cours" tant qu'au
 * moins un de ses lots ne l'est pas.
 */

const MODES = ['user', 'vendor', 'accounting_firm', 'all']

function emptyByMode() {
	return MODES.reduce((acc, mode) => {
		acc[mode] = []
		return acc
	}, {})
}

export default {
	namespaced: true,
	// Fonction plutôt qu'objet littéral : ce module est instancié dans plusieurs
	// `Vuex.Store` distincts (chaque test de `PasswordExpiration.spec.js` en crée
	// un nouveau) - un état littéral serait partagé par référence entre toutes
	// ces instances (piège classique des modules Vuex réutilisables), faisant
	// fuiter l'état d'un test vers le suivant.
	state: () => ({
		byMode: emptyByMode(),
	}),
	getters: {
		batchesForMode: state => mode => state.byMode[mode] || [],
		isModeRunning: (state, getters) => mode => getters.batchesForMode(mode).some(batch => !batch.finished),
		aggregateForMode: (state, getters) => mode => {
			const batches = getters.batchesForMode(mode)
			if (!batches.length) {
				return null
			}

			const total = batches.reduce((sum, batch) => sum + (batch.total || 0), 0)
			const processed = batches.reduce((sum, batch) => sum + (batch.processed || 0), 0)
			const failed = batches.reduce((sum, batch) => sum + (batch.failed || 0), 0)
			const pending = batches.reduce((sum, batch) => sum + (batch.pending || 0), 0)
			const finished = batches.every(batch => batch.finished)
			const progress = total > 0 ? Math.round((processed / total) * 100) : 0

			return { total, processed, failed, pending, finished, progress }
		},
	},
	mutations: {
		setBatches(state, { mode, batches }) {
			state.byMode = { ...state.byMode, [mode]: batches }
		},
		updateBatch(state, { mode, batch }) {
			const batches = (state.byMode[mode] || []).map(existing =>
				existing.batch_id === batch.batch_id ? { ...existing, ...batch } : existing
			)
			state.byMode = { ...state.byMode, [mode]: batches }
		},
		clearMode(state, mode) {
			state.byMode = { ...state.byMode, [mode]: [] }
		},
	},
	actions: {
		setBatches({ commit }, payload) {
			commit('setBatches', payload)
		},
		updateBatch({ commit }, payload) {
			commit('updateBatch', payload)
		},
		clearMode({ commit }, mode) {
			commit('clearMode', mode)
		},
	},
}
